import type { ExternalSourceHeader } from "./externalSource";

export interface RequestRow extends ExternalSourceHeader { enabled: boolean }
export type RequestAuth =
  | { type: "none" }
  | { type: "apiKey"; key: string; value: string; destination: "header" | "query" }
  | { type: "bearer"; token: string }
  | { type: "basic"; username: string; password: string };
export interface RequestEditorState {
  version: 1;
  url: string;
  params: RequestRow[];
  headers: RequestRow[];
  auth: RequestAuth;
}
const headerName = /^[!#$%&'*+.^_`|~0-9A-Za-z-]+$/;
export function paramsFromURL(url: string): RequestRow[] {
  try { return [...new URL(url).searchParams].map(([key, value]) => ({ key, value, enabled: true })); }
  catch { return []; }
}
export function editRequestURL(state: RequestEditorState, url: string): RequestEditorState {
  return { ...state, url, params: [...paramsFromURL(url), ...state.params.filter(row => !row.enabled)] };
}
export function editRequestParams(state: RequestEditorState, params: RequestRow[]): RequestEditorState {
  try {
    const url = new URL(state.url);
    url.search = "";
    params.filter(row => row.enabled && row.key).forEach(row => url.searchParams.append(row.key, row.value));
    return { ...state, params, url: url.href };
  } catch { return { ...state, params }; }
}
export function generatedRequestEntry(auth: RequestAuth): { destination: "header" | "query"; key: string; value: string } | undefined {
  if (auth.type === "none") return undefined;
  if (auth.type === "apiKey") return { destination: auth.destination, key: auth.key, value: auth.value };
  if (auth.type === "bearer") return { destination: "header", key: "Authorization", value: `Bearer ${auth.token}` };
  const bytes = new TextEncoder().encode(`${auth.username}:${auth.password}`);
  return { destination: "header", key: "Authorization", value: `Basic ${btoa(Array.from(bytes, byte => String.fromCharCode(byte)).join(""))}` };
}
export function compileRequest(state: RequestEditorState): { endpointURL: string; headers: ExternalSourceHeader[]; errors: string[] } {
  const errors: string[] = [];
  let url: URL | undefined;
  try { url = new URL(state.url); } catch { errors.push("Enter a valid endpoint URL."); }
  if (url && !["http:", "https:"].includes(url.protocol)) errors.push("The endpoint must use http or https.");
  if (url && (url.username || url.password)) errors.push("Use the Authorization tab for credentials instead of URL user information.");
  const headers = state.headers.filter(row => row.enabled && (row.key || row.value)).map(({ key, value }) => ({ key, value }));
  const names = new Set<string>();
  for (const row of headers) {
    if (!headerName.test(row.key)) errors.push("Header names must use valid HTTP header characters.");
    if (/[\r\n]/.test(row.value)) errors.push("Header values cannot contain line breaks.");
    if (names.has(row.key.toLowerCase())) errors.push(`Duplicate header: ${row.key}.`);
    names.add(row.key.toLowerCase());
  }
  if (state.params.some(row => row.enabled && !row.key && row.value)) errors.push("Query parameters with values need a name.");
  const auth = state.auth;
  if (auth.type === "apiKey" && (!auth.key || !auth.value)) errors.push("Enter the API key name and value.");
  if (auth.type === "apiKey" && auth.destination === "header" && !headerName.test(auth.key)) errors.push("Enter a valid API key header name.");
  if (auth.type === "bearer" && !auth.token) errors.push("Enter a bearer token.");
  if (auth.type === "basic" && !auth.username) errors.push("Enter a Basic Auth username.");
  if (auth.type === "basic" && auth.username.includes(":")) errors.push("Basic Auth usernames cannot contain a colon.");
  const generated = generatedRequestEntry(auth);
  let endpointURL = state.url.trim();
  if (generated) {
    if (generated.destination === "header") {
      if (names.has(generated.key.toLowerCase())) errors.push(`Remove or disable the manual ${generated.key} header; it is managed by Authorization.`);
      if (/[\r\n]/.test(generated.value)) errors.push("Authorization header values cannot contain line breaks.");
      headers.push({ key: generated.key, value: generated.value });
    } else if (url) {
      if (url.searchParams.has(generated.key)) errors.push(`Remove or disable the manual ${generated.key} query parameter; it is managed by Authorization.`);
      url.searchParams.append(generated.key, generated.value);
      endpointURL = url.href;
    }
  }
  return { endpointURL, headers, errors: [...new Set(errors)] };
}
export function requireCompiledRequest(state: RequestEditorState) {
  const result = compileRequest(state);
  if (result.errors.length) throw new Error(result.errors.join(" "));
  return { endpointURL: result.endpointURL, headers: result.headers };
}
function parseAuth(value: unknown): RequestAuth | undefined {
  if (!value || typeof value !== "object") return undefined;
  const auth = value as Record<string, unknown>;
  switch (auth.type) {
    case "none": return { type: "none" };
    case "bearer":
      return typeof auth.token === "string" ? { type: "bearer", token: auth.token } : undefined;
    case "basic":
      return typeof auth.username === "string" && typeof auth.password === "string"
        ? { type: "basic", username: auth.username, password: auth.password } : undefined;
    case "apiKey":
      return typeof auth.key === "string" && typeof auth.value === "string" &&
        (auth.destination === "header" || auth.destination === "query")
        ? { type: "apiKey", key: auth.key, value: auth.value, destination: auth.destination } : undefined;
    default: return undefined;
  }
}
export function parseRequestEditor(value: unknown): RequestEditorState | undefined {
  if (!value || typeof value !== "object") return undefined;
  const data = value as Record<string, unknown>;
  const rows = (value: unknown): value is RequestRow[] => Array.isArray(value) && value.every(row =>
    row && typeof row.key === "string" && typeof row.value === "string" && typeof row.enabled === "boolean");
  const auth = parseAuth(data.auth);
  if (data.version !== 1 || typeof data.url !== "string" || !rows(data.params) || !rows(data.headers) || !auth) return undefined;
  const copyRows = (rows: RequestRow[]) => rows.map(({ key, value, enabled }) => ({ key, value, enabled }));
  return { version: 1, url: data.url, params: copyRows(data.params), headers: copyRows(data.headers), auth };
}
export function hydrateRequestEditor(source?: { endpointURL?: string; headers: ExternalSourceHeader[]; requestEditor?: RequestEditorState } | null) {
  const fallback: RequestEditorState = { version: 1, url: source?.endpointURL ?? "", params: paramsFromURL(source?.endpointURL ?? ""), headers: (source?.headers ?? []).map(row => ({ ...row, enabled: true })), auth: { type: "none" } };
  const saved = source?.requestEditor;
  if (saved) {
    const compiled = compileRequest(saved);
    const normalize = (headers: ExternalSourceHeader[]) => JSON.stringify(headers.map(row => [row.key.toLowerCase(), row.value]).sort((a,b) => a[0].localeCompare(b[0])));
    if (!compiled.errors.length && compiled.endpointURL === source.endpointURL && normalize(compiled.headers) === normalize(source.headers) && JSON.stringify(paramsFromURL(saved.url)) === JSON.stringify(saved.params.filter(row => row.enabled && row.key))) return { state: saved, notice: undefined };
  }
  return { state: fallback, notice: saved ? "The saved request changed in another client. Its current URL and headers have been loaded as manual entries." : undefined };
}
export function maskedRequestPreview(state: RequestEditorState): string {
  const compiled = compileRequest(state);
  let urlText = "Enter a valid endpoint URL";
  try {
    const url = new URL(compiled.endpointURL);
    url.username = ""; url.password = "";
    const params = [...url.searchParams].map(([key]) => [key, "••••"]);
    url.search = "";
    params.forEach(([key,value]) => url.searchParams.append(key,value));
    urlText = url.href;
  } catch { /* Never echo invalid URLs that could contain credentials. */ }
  return `GET ${urlText}\n${compiled.headers.map(row => `${row.key}: ••••`).join("\n")}`;
}
